<template>
  <div :class="responsive ? responsiveClasses : ''">
    <table v-bind="$attrs" :class="tableClasses">
      <!-- 1) colgroup for fixed widths -->
      <colgroup>
        <slot name="table-colgroup" />
      </colgroup>

      <!-- 2) caption at top: either named slot or prop -->
      <template v-if="$slots['table-caption']">
        <caption>
          <slot name="table-caption" />
        </caption>
      </template>
      <caption v-else-if="caption && captionTop">
        {{
          caption
        }}
      </caption>

      <!-- 3) header -->
      <thead :class="theadClasses">
        <tr>
          <th
            v-for="field in computedFields"
            :key="field.key"
            :class="field.class"
          >
            <slot
              :name="`head(${field.key})`"
              :label="field.label"
              :field="field"
              >{{ field.label }}</slot
            >
          </th>
        </tr>
      </thead>

      <!-- 4) body -->
      <tbody>
        <tr
          v-for="(item, rowIndex) in pagedItems"
          :key="getRowKey(item, globalRowIndex(rowIndex))"
          :class="resolveTbodyClass(item, rowIndex)"
          @click="$emit('row-clicked', item, globalRowIndex(rowIndex), $event)"
          @dblclick="
            $emit('row-dblclicked', item, globalRowIndex(rowIndex), $event)
          "
          @contextmenu.prevent="
            $emit('row-contextmenu', item, globalRowIndex(rowIndex), $event)
          "
        >
          <td v-for="field in computedFields" :key="field.key">
            <slot
              :name="`cell(${field.key})`"
              :item="item"
              :value="field.getValue(item, rowIndex)"
              :field="field"
              :index="rowIndex"
            >
              {{ field.getValue(item, rowIndex) }}
            </slot>
          </td>
        </tr>
        <tr v-if="!items.length && showEmpty">
          <td :colspan="computedFields.length" class="text-center">
            {{ emptyText }}
          </td>
        </tr>
      </tbody>

      <!-- 5) optional footer -->
      <tfoot v-if="footVariant">
        <tr :class="`table-${footVariant}`">
          <td v-for="field in computedFields" :key="field.key" />
        </tr>
      </tfoot>

      <!-- 6) caption at bottom if not top -->
      <template v-if="$slots['table-caption-bottom']">
        <!-- if you want a separate bottom‐slot, you can add this -->
        <caption>
          <slot name="table-caption-bottom" />
        </caption>
      </template>
      <caption v-else-if="caption && !captionTop">
        {{
          caption
        }}
      </caption>
    </table>
  </div>
</template>

<script>
export default {
  name: "BaseTable",
  inheritAttrs: false,
  props: {
    rowKey: { type: [String, Function], default: "id" },
    items: { type: Array, default: () => [] },
    fields: { type: [Array, Object], default: () => [] },

    striped: { type: Boolean, default: false },
    bordered: { type: Boolean, default: false },
    borderless: { type: Boolean, default: false },
    hover: { type: Boolean, default: false },
    small: { type: Boolean, default: false },
    dark: { type: Boolean, default: false },

    responsive: { type: [Boolean, String], default: false },

    headVariant: { type: String, default: null },
    theadClass: { type: [String, Array], default: null },
    footVariant: { type: String, default: null },

    caption: { type: String, default: null },
    captionTop: { type: Boolean, default: false },

    showEmpty: { type: Boolean, default: true },
    emptyText: { type: String, default: "There are no records to display" },

    perPage: { type: Number, default: null },
    currentPage: { type: Number, default: 1 },

    tbodyTrClass: { type: [String, Function, Array], default: null },
  },
  computed: {
    tableClasses() {
      const cls = ["table"];
      this.striped && cls.push("table-striped");
      this.bordered && cls.push("table-bordered");
      this.borderless && cls.push("table-borderless");
      this.hover && cls.push("table-hover");
      this.small && cls.push("table-sm");
      this.dark && cls.push("table-dark");
      return cls;
    },
    responsiveClasses() {
      if (typeof this.responsive === "string") {
        return [`table-responsive-${this.responsive}`];
      }
      return this.responsive ? ["table-responsive"] : [];
    },
    theadClasses() {
      const c = [];
      this.headVariant && c.push(`thead-${this.headVariant}`);
      this.theadClass && c.push(this.theadClass);
      return c;
    },
    computedFields() {
      let arr = [];
      if (Array.isArray(this.fields) && this.fields.length) {
        arr = this.fields;
      } else if (this.items.length) {
        arr = Object.keys(this.items[0]);
      }
      return arr.map((f) => {
        const key = typeof f === "string" ? f : f.key;
        const label = typeof f === "string" ? f : f.label || f.key;
        const cls = (typeof f === "object" && f.class) || "";
        const getValue = key.includes(".")
          ? (item) => key.split(".").reduce((o, k) => (o || {})[k], item)
          : (item) => item[key];
        return { key, label, class: cls, getValue };
      });
    },
    pagedItems() {
      if (!this.perPage) return this.items;
      const start = (this.currentPage - 1) * this.perPage;
      return this.items.slice(start, start + this.perPage);
    },
  },
  methods: {
    globalRowIndex(idx) {
      return this.perPage ? (this.currentPage - 1) * this.perPage + idx : idx;
    },
    getRowKey(item, idx) {
      if (typeof this.rowKey === "function") {
        return this.rowKey(item, idx);
      }
      return item._rowKey ?? item[this.rowKey] ?? idx;
    },
    resolveTbodyClass(item, rowIndex) {
      return typeof this.tbodyTrClass === "function"
        ? this.tbodyTrClass(item, rowIndex)
        : this.tbodyTrClass;
    },
  },
};
</script>
